// src/components/tesoreria/HistorialImportacionesTesoreria.jsx
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  History,
  Warning as WarningIcon
} from '@mui/icons-material';
import { formatearFecha, formatearMonto } from '../../utils/formatters';
import tesoreriaService from '../../services/tesoreriaService';

const HistorialImportacionesTesoreria = ({ rutEmpresa, anio, mes, onImportacionEliminada }) => {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eliminando, setEliminando] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  useEffect(() => {
    if (rutEmpresa) {
      cargarHistorial();
    }
  }, [rutEmpresa, anio, mes]);

  const cargarHistorial = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await tesoreriaService.listarImportaciones(rutEmpresa, anio, mes);
      setHistorial(data);
    } catch (err) {
      console.error("Error al cargar historial de importaciones:", err);
      setError(err.message || 'Error al cargar el historial de importaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (importacionId) => {
    setEliminando(importacionId);
    try {
      await tesoreriaService.eliminarImportacion(importacionId);
      setHistorial(historial.filter(imp => imp.id !== importacionId));
      setShowConfirmDelete(null);
      setError('');

      // Llamar el callback para refrescar los movimientos
      if (onImportacionEliminada) {
        onImportacionEliminada();
      }
    } catch (err) {
      console.error("Error al eliminar importación:", err);
      setError(err.message || 'Error al eliminar la importación');
    } finally {
      setEliminando(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Historial de Importaciones
        </Typography>

        <Button
          variant="outlined"
          startIcon={<History />}
          onClick={cargarHistorial}
          disabled={loading}
          size="small"
        >
          Actualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : historial.length === 0 ? (
        <Alert severity="info">
          No hay importaciones registradas para este período.
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total Movimientos</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.map((importacion) => (
                <TableRow key={importacion.id} hover>
                  <TableCell>{formatearFecha(importacion.fecha_importacion)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                      {importacion.descripcion}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{importacion.total_movimientos}</TableCell>
                  <TableCell align="right">{formatearMonto(importacion.monto_total)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={importacion.estado}
                      size="small"
                      color={importacion.estado === 'COMPLETADA' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Eliminar importación">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setShowConfirmDelete(importacion.id)}
                        disabled={eliminando === importacion.id}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={showConfirmDelete !== null}
        onClose={() => setShowConfirmDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmar eliminación
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            ¿Está seguro que desea eliminar esta importación? Se eliminarán todos los movimientos asociados.
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmDelete(null)}
            disabled={eliminando === showConfirmDelete}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleEliminar(showConfirmDelete)}
            color="error"
            variant="contained"
            disabled={eliminando === showConfirmDelete}
          >
            {eliminando === showConfirmDelete ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HistorialImportacionesTesoreria;
